import {CancelButton} from '@hconnect/uikit/src/lib2'
import {Check} from '@mui/icons-material'
import {LoadingButton} from '@mui/lab'
import {Box} from '@mui/material'
import {isEmpty, isEqual} from 'lodash'
import React, {FC, useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {validateActionItem} from '../common/validator/actionValidator'
import {
  ActionForm,
  ActionFormFieldKey,
  ActionsDetails,
  ActionCreatorInfo,
  ActionsMoreButton,
  MoveQuestionToDone
} from '../components'
import {Attachments} from '../components/common'
import {FEATURE_NAMESPACE} from '../consts'
import {
  useAddActionAttachments,
  useDeleteActionAttachment,
  useEditAction,
  usePlantUpmId,
  useTimeZone,
  useSectionById,
  useActionId
} from '../hooks'
import {ActionItem} from '../types'

import {SideCardContainer} from './SideCardContainer'

type ActionSideCardContainerProps = {
  selectedAction?: ActionItem
  showApprovers?: boolean
}

export const ActionSideCardContainer: FC<ActionSideCardContainerProps> = ({
  selectedAction,
  showApprovers
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const plantId = usePlantUpmId()
  const timeZone = useTimeZone()
  const [, setActionId] = useActionId()
  const [isEditMode, setIsEditMode] = useState(false)
  const [formValues, setFormValues] = React.useState<Partial<ActionItem>>({})

  const onEdit = useCallback(
    (action: ActionItem) => {
      setActionId(action.id)
    },
    [setActionId]
  )

  const onActionDetailsClose = useCallback(() => {
    setActionId(undefined)
  }, [setActionId])

  const validationErrors = useMemo(
    () => (formValues ? validateActionItem(formValues) : {}),
    [formValues]
  )
  const isFormValid = isEmpty(validationErrors)

  const handleInputChange = <K extends ActionFormFieldKey>(
    key: K,
    value: Partial<ActionItem>[K]
  ) => {
    setFormValues((values) => ({...values, [key]: value}))
  }

  const handleClose = () => {
    setIsEditMode(false)
    setFormValues({})
  }

  const [showMoveQuesDialog, setShowMoveQuesDialog] = useState<boolean>(false)
  const {mutate: editAction, isPending} = useEditAction(({showMoveQuestToDone}) => {
    handleClose()
    showMoveQuestToDone && setShowMoveQuesDialog(true)
  })
  const {mutate: addAttachments, isPending: uploading} = useAddActionAttachments()
  const {mutate: deleteAttachment} = useDeleteActionAttachment()

  const {data: section} = useSectionById(selectedAction?.section?.id)

  const questionAnswer = useMemo(
    () =>
      section?.topics
        .find((topic) => selectedAction?.topic?.id === topic.id)
        ?.questions?.find((question) => selectedAction?.questionId === question.id)?.submission
        ?.answer,
    [section?.topics, selectedAction?.questionId, selectedAction?.topic?.id]
  )

  const handleSubmit = () => {
    editAction({actionItem: formValues as ActionItem, questionAnswer})
  }

  const saveDisabled = isEqual(selectedAction, formValues)

  return (
    <>
      {selectedAction && !isEditMode && (
        <SideCardContainer
          title={selectedAction.title}
          onClose={onActionDetailsClose}
          underTitleComponent={<ActionCreatorInfo action={selectedAction} timeZone={timeZone} />}
          actions={
            <ActionsMoreButton
              moreButtonTestId="action-card-more-button"
              item={selectedAction}
              showApprovers={showApprovers}
              enableEditMode={(actionToEdit) => {
                setActionId(actionToEdit.id)
                setFormValues(actionToEdit)
                setIsEditMode(true)
              }}
            />
          }
        >
          <ActionsDetails
            onChange={onEdit}
            action={selectedAction}
            questionAnswer={questionAnswer}
            showApprovers={showApprovers}
          />
        </SideCardContainer>
      )}
      {selectedAction && isEditMode && formValues && (
        <SideCardContainer title={t('editAction.heading')} data-test-id="action-edit-form">
          <ActionForm
            isEditMode={isEditMode}
            formValues={formValues}
            validationErrors={validationErrors}
            handleInputChange={handleInputChange}
            actionItem={selectedAction}
            showApprovers={showApprovers}
          />
          <Attachments
            attachedFiles={selectedAction.attachments}
            onUpload={(files) =>
              addAttachments({plantId, actionId: Number(selectedAction.id), files})
            }
            onDelete={(attachment) =>
              deleteAttachment({plantId, actionId: Number(selectedAction.id), attachment})
            }
            isUploading={uploading}
            inputId="upload-action-sidecard-attachment-input"
          />
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <CancelButton onClick={handleClose} data-test-id="cancel-action">
              {t('button.cancel')}
            </CancelButton>
            <LoadingButton
              variant="contained"
              onClick={() => handleSubmit()}
              disabled={!isFormValid || saveDisabled}
              startIcon={<Check />}
              data-test-id="save-action"
              loading={isPending}
              loadingPosition={'start'}
            >
              {t('button.save')}
            </LoadingButton>
          </Box>
        </SideCardContainer>
      )}

      {showMoveQuesDialog && selectedAction && (
        <MoveQuestionToDone
          actionItem={selectedAction}
          onClose={() => setShowMoveQuesDialog(false)}
        />
      )}
    </>
  )
}
